import { Flex, SearchFilterTag, theme } from '@/components';
import { useTagOptions } from '@/features/tag/hooks';
import { useHierarchyLabels } from '@/features/tenant-manager/hierarchy-label/_hooks/hierarchy-label';
import {
  ActionRequiredLabelModifier,
  type WorkflowSearchFormSchema,
  WorkflowStatusLabelModifier,
} from '@/features/workflow/models/search';
import { getCreatedMonthValueText } from '@/utils';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

type FormValues = z.infer<typeof WorkflowSearchFormSchema>;

type FilterTag<T> = {
  label: string | undefined;
} & ({ closable: false } | { closable: true; clearValue: T });

type FilterTags = {
  [K in keyof FormValues]?: FilterTag<FormValues[K]>;
};

export const WorkflowListSearchFilterTags: FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const tagOptions = useTagOptions({ type: 'workflow' });

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });

  const { setValue, watch } = useFormContext<FormValues>();

  const [
    createdMonthRange,
    actionRequired,
    keyword,
    stageLabelId,
    tagIds,
    workflowStatus,
  ] = watch([
    'createdMonthRange',
    'actionRequired',
    'keyword',
    'stageLabelId',
    'tagIds',
    'workflowStatus',
  ]);

  const filterTags = {
    createdMonthRange: createdMonthRange?.some((v) => !!v)
      ? {
          label: t('prefix-created-month-with-value', {
            value: getCreatedMonthValueText(createdMonthRange),
          }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    actionRequired: actionRequired
      ? {
          label: t('workflow.action-required-with-value', {
            value: ActionRequiredLabelModifier[actionRequired],
          }),
          closable: false,
        }
      : undefined,
    keyword: keyword
      ? {
          label: t('prefix-keyword-with-value', { value: keyword }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    stageLabelId: stageLabelId
      ? {
          label: t('workflow.prefix.stage-label-with-value', {
            value:
              hierarchyLabelOptions?.find((d) => d.value === stageLabelId)
                ?.label ?? t('not-exist-in-option'),
          }),
          closable: true,
          clearValue: null,
        }
      : undefined,
    tagIds: tagIds.length
      ? {
          label: tagOptions.data
            ?.flatMap((t) => (tagIds.includes(t.value) ? [t.label] : []))
            .join(', '),
          closable: true,
          clearValue: [],
        }
      : undefined,
    workflowStatus: workflowStatus
      ? {
          label: t('workflow.prefix.status-with-value', {
            value: WorkflowStatusLabelModifier[workflowStatus],
          }),
          closable: false,
        }
      : undefined,
  } as const satisfies FilterTags;

  return (
    <Flex
      style={{
        background: token.colorBgLayout,
        padding: 5,
        gap: 4,
        flexWrap: 'wrap',
        display: 'inline-flex',
      }}
    >
      {Object.entries(filterTags).map(
        ([key, value]) =>
          value && (
            <SearchFilterTag
              key={key}
              label={value.label}
              closable={value.closable}
              onClose={() => {
                invariant(value.closable, t('unreachable-code'));
                setValue(key as keyof FormValues, value.clearValue);
              }}
            />
          ),
      )}
    </Flex>
  );
};
